import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface UserRoleState {
  isAdmin: boolean;
  isAttorney: boolean;
  isClient: boolean;
  isDelegate: boolean;
  isInHouseCounsel: boolean;
  loading: boolean;
  lawyerData: any | null;
  lawFirmData: any | null;
  delegateData: any | null;
  inHouseCounselData: any | null;
}

interface UserRoleContextValue extends UserRoleState {
  hasAttorneyAccess: () => boolean;
  hasClientAccess: () => boolean;
  isExistingLawyer: () => boolean;
  hasActiveLawFirm: () => boolean;
  isTrialExpired: () => boolean;
  hasDelegatePermission: (permission: string) => boolean;
  refetch: () => Promise<void>;
}

const UserRoleContext = createContext<UserRoleContextValue | null>(null);

// Global cache to prevent duplicate requests across multiple context consumers
const roleCache = new Map<string, UserRoleState>();
const pendingRequests = new Map<string, Promise<UserRoleState>>();

interface UserRoleProviderProps {
  children: ReactNode;
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState<UserRoleState>({
    isAdmin: false,
    isAttorney: false,
    isClient: false,
    isDelegate: false,
    isInHouseCounsel: false,
    loading: true,
    lawyerData: null,
    lawFirmData: null,
    delegateData: null,
    inHouseCounselData: null,
  });
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const fetchRoleData = async (): Promise<UserRoleState> => {
    const defaultState: UserRoleState = {
      isAdmin: false,
      isAttorney: false,
      isClient: false,
      isDelegate: false,
      isInHouseCounsel: false,
      loading: false,
      lawyerData: null,
      lawFirmData: null,
      delegateData: null,
      inHouseCounselData: null,
    };

    if (!user) return defaultState;

    const cacheKey = `user_role_${user.id}`;
    
    if (roleCache.has(cacheKey)) {
      return { ...roleCache.get(cacheKey)!, loading: false };
    }

    if (pendingRequests.has(cacheKey)) {
      const result = await pendingRequests.get(cacheKey)!;
      return { ...result, loading: false };
    }

    const requestPromise = (async () => {
      try {
        console.log('[UserRoleContext] 🚀 Fetching role data in parallel for user:', user.id);

        const [rolesResult, lawyerResult, delegateResult, inHouseResult] = await Promise.allSettled([
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id),
          supabase
            .from('lawyers')
            .select(`
              *,
              firms:firm_id (
                id,
                name,
                is_active
              )
            `)
            .eq('user_id', user.id)
            .eq('is_active', true)
            .maybeSingle(),
          supabase
            .from('client_delegates')
            .select(`
              *,
              clients:client_id (
                id,
                first_name,
                last_name,
                company,
                user_id
              ),
              firms:firm_id (
                id,
                name,
                is_active
              )
            `)
            .or(`user_id.eq.${user.id},email.eq.${user.email?.toLowerCase()}`)
            .eq('is_active', true)
            .maybeSingle(),
          supabase
            .from('in_house_counsel')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .maybeSingle()
        ]);

        const roles = rolesResult.status === 'fulfilled' ? rolesResult.value.data : [];
        const lawyerInfo = lawyerResult.status === 'fulfilled' ? lawyerResult.value.data : null;
        const lawyerError = lawyerResult.status === 'fulfilled' ? lawyerResult.value.error : null;
        const delegateInfo = delegateResult.status === 'fulfilled' ? delegateResult.value.data : null;
        const inHouseInfo = inHouseResult.status === 'fulfilled' ? inHouseResult.value.data : null;

        const userRoles = roles?.map((r: any) => r.role) || [];
        const hasActiveLawyer = !lawyerError && !!lawyerInfo;
        const hasActiveDelegate = !!delegateInfo;
        const hasActiveInHouse = !!inHouseInfo;
        
        const isAdmin = userRoles.includes('admin');
        let isAttorney =
          hasActiveLawyer ||
          userRoles.includes('lawyer') ||
          userRoles.includes('law_firm_admin') ||
          userRoles.includes('law_firm_member');
        // Guard against stale role rows: an admin without an active lawyer
        // profile must resolve as admin, not attorney — otherwise portal
        // resolution sends them to the attorney portal and it hangs because
        // there is no lawyer record to load.
        if (isAdmin && !hasActiveLawyer) {
          isAttorney = false;
        }
        const isInHouseCounsel = hasActiveInHouse || userRoles.includes('in_house_counsel');
        const isDelegate = hasActiveDelegate && !isAdmin && !isAttorney && !isInHouseCounsel;
        const isClient = !isAdmin && !isAttorney && !isInHouseCounsel && !isDelegate;

        const result: UserRoleState = {
          isAdmin,
          isAttorney,
          isClient,
          isDelegate,
          isInHouseCounsel,
          loading: false,
          lawyerData: hasActiveLawyer ? lawyerInfo : null,
          lawFirmData: hasActiveLawyer && lawyerInfo?.firms ? lawyerInfo.firms : null,
          delegateData: hasActiveDelegate ? delegateInfo : null,
          inHouseCounselData: hasActiveInHouse ? inHouseInfo : null,
        };

        // Only cache results that resolved meaningful role data
        // Skip caching if we got no roles AND no lawyer AND no delegate AND no in-house
        // (likely means auth.uid() wasn't ready during the query)
        const hasAnyRoleData = (roles && roles.length > 0) || hasActiveLawyer || hasActiveDelegate || hasActiveInHouse;
        if (hasAnyRoleData) {
          roleCache.set(cacheKey, result);
          setTimeout(() => roleCache.delete(cacheKey), 5 * 60 * 1000);
        } else {
          console.warn('[UserRoleContext] ⚠️ No role data found — not caching (may retry)');
        }

        return result;
      } catch (error) {
        console.error('[UserRoleContext] ❌ Error fetching role data:', error);
        return defaultState;
      }
    })();

    pendingRequests.set(cacheKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  };

  const refetch = async () => {
    if (user) {
      roleCache.delete(`user_role_${user.id}`);
      setResolvedUserId(null);
      setState(prev => ({ ...prev, loading: true }));
    }
    const result = await fetchRoleData();
    if (mounted.current) {
      setState(result);
      setResolvedUserId(user?.id ?? null);
    }
  };

  useEffect(() => {
    if (!user) {
      setResolvedUserId(null);
      setState({
        isAdmin: false,
        isAttorney: false,
        isClient: false,
        isDelegate: false,
        isInHouseCounsel: false,
        loading: false,
        lawyerData: null,
        lawFirmData: null,
        delegateData: null,
        inHouseCounselData: null,
      });
      return;
    }

    setResolvedUserId(null);
    setState(prev => ({ ...prev, loading: true }));

    const loadRole = async () => {
      const result = await fetchRoleData();
      if (mounted.current) {
        setState(result);
        setResolvedUserId(user.id);
      }
    };
    
    loadRole();
  }, [user?.id]);

  const effectiveLoading = !!user && (state.loading || resolvedUserId !== user.id);

  const contextValue: UserRoleContextValue = {
    ...state,
    loading: effectiveLoading,
    hasAttorneyAccess: () => state.isAdmin || state.isAttorney,
    hasClientAccess: () => !user || state.isClient || state.isDelegate || (!state.isAttorney && !state.isAdmin),
    isExistingLawyer: () => state.lawyerData && state.lawyerData.is_active,
    hasActiveLawFirm: () => state.lawFirmData && state.lawFirmData.is_active,
    isTrialExpired: () => false,
    hasDelegatePermission: (permission: string) => {
      if (!state.isDelegate || !state.delegateData) return false;
      return (state.delegateData.permissions || []).includes(permission);
    },
    refetch,
  };

  return (
    <UserRoleContext.Provider value={contextValue}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRoleContext = (): UserRoleContextValue => {
  const context = useContext(UserRoleContext);
  
  if (!context) {
    throw new Error('useUserRoleContext must be used within a UserRoleProvider');
  }
  
  return context;
};

export const useOptionalUserRoleContext = (): UserRoleContextValue | null => {
  return useContext(UserRoleContext);
};
